import React, { useEffect } from "react";
import clsx from "clsx";
/* eslint-disable import/no-extraneous-dependencies */
import Popover from "@material-ui/core/Popover";
/* eslint-enable */

import { IconCancel, IconChevronDown } from "@origin-digital/ods-icons";

import {
  Box,
  IconButton,
  makeStylesSeeded,
  Stack,
} from "@origin-digital/ods-core";
import { NavLink } from "../NavLink";
import { INavConfig } from "../../AuthNav/NavConfig";

const useStyles = makeStylesSeeded(({ palette }) => ({
  popover: {
    pointerEvents: "none",
  },
  popOverPaper: {
    pointerEvents: "auto",
    boxShadow: "0px 0px 8px 0px rgba(0,0,0,0.08)",
    borderRadius: 0,
  },
  popOverContent: {
    minWidth: 380,
    minHeight: "max-content",
    padding: 32,
    display: "flex",
    alignItems: "center",
  },
  dropdownContainer: {
    cursor: "pointer",

    "&:focus": {
      outline: "1px dotted #212121, 5px auto -webkit-focus-ring-color",
    },

    '&[aria-owns="mouse-over-popover"] *': {
      color: palette.primary.main,
    },
  },
  closeIcon: {
    top: 16,
    right: 16,
  },
  parentLinkContainer: {
    cursor: "pointer",
  },
}));

interface IKrakenDropdown {
  dropdownList: INavConfig[];
  id: string;
  children: React.ReactNode;
}

export const KrakenDropdown = (props: IKrakenDropdown) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const { dropdownList, children, id } = props;
  const open = !!anchorEl;
  const styles = useStyles({ open });

  const handlePopoverOpen = () => {
    setAnchorEl(document.getElementById(id));
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handlePopoverToggle = () => {
    if (open) {
      handlePopoverClose();
    } else {
      handlePopoverOpen();
    }
  };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.target as Element).id === id) {
        if (["Space", "Enter", "ArrowDown"].includes(event.code)) {
          handlePopoverOpen();
        }
      }
    };

    document.addEventListener("keydown", listener);

    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  return (
    <Box
      id={id}
      data-id={id}
      display="inline-flex"
      aria-owns={open ? "mouse-over-popover" : undefined}
      aria-haspopup="true"
      className={clsx(styles.dropdownContainer)}
      tabIndex={0}
      onClick={handlePopoverToggle}
      domProps={{
        onMouseEnter: handlePopoverOpen,
        onMouseLeave: handlePopoverClose,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        className={clsx(styles.parentLinkContainer)}
      >
        {children}
        <IconChevronDown tone="grey300" />
      </Box>
      <Popover
        data-id="dropdown-popover"
        className={clsx(styles.popover)}
        classes={{
          paper: styles.popOverPaper,
        }}
        disableEnforceFocus
        disableRestoreFocus
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
      >
        <Box className={clsx(styles.popOverContent)}>
          <Stack space="medium">
            {dropdownList.map((navLink, index) => (
              <NavLink linkConfig={navLink} key={index} />
            ))}
          </Stack>
          <Box position="absolute" className={clsx(styles.closeIcon)}>
            <IconButton
              onClick={handlePopoverClose}
              onBlur={handlePopoverClose}
            >
              <IconCancel color="critical" />
            </IconButton>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
